import React, { FunctionComponent } from 'react';

import { AutomationForm } from '@components/bulk_management/automation/form/AutomationForm';
import { EditOrCreate, Views } from '@components/bulk_management/automation/AutomationEnums';
import { AutomationShowResponse } from '@components/bulk_management/automation/AutomationTypes';
import { transformIncludedActions, transformTriggers } from '@components/bulk_management/automation/helpers';

import { BFLoader } from '@components/common/loader/main';

import { FontIcons } from '@components/library/icon';
import { TextButton } from '@components/library/button';

import { useFetch } from '@api/ApiHelper';

import { localizeDateTime } from '@helpers/localize';

interface DetailsViewProps {
  automationKey: string;
  isCopy: boolean;
  setView: SetStateDispatch<Views>;
}

export const DetailsView: FunctionComponent<DetailsViewProps> = ({
  automationKey,
  isCopy,
  setView
}) => {
  const getAutomationFetch = useFetch<AutomationShowResponse>({
    fetchOnMount: true,
    fields: ['creator', 'created_at', 'last_executed'],
    params: {
      include: 'actions',
    },
    url: `/api/v4/private/automations/${automationKey}`
  });

  if (getAutomationFetch.loading) {
    return <BFLoader />;
  }

  if (getAutomationFetch.error) {
    // go back to the home view and show an error
    Notify.create({ title: bfTranslate('Oops! Something went wrong.'), type: 'error' });
    setView(Views.Home);
    return null;
  }

  const {
    name,
    creator,
    created_at: createdAt,
    last_executed: lastExecuted
  } = getAutomationFetch.response.data.attributes;

  return (
    <div className="details">
      <TextButton
        className="return-to-home-button"
        icon={FontIcons.ArrowLeft}
        onClick={(): void => setView(Views.Home)}
      >
        {bfTranslate('Back to Automation Home')}
      </TextButton>
      {isCopy
        ? <h2>{bfTranslate('Create Automation')}</h2>
        : (
          <>
            <h2 className="details__title">{name}</h2>
            <hr />
            <div className="details__summary">
              <label className="details__summary--label">
                <h3 className="details__summary--label--title">{bfTranslate('Created by')}</h3>
                <span className="details__summary--copy details__created-by">{creator}</span>
              </label>
              <label className="details__summary--label">
                <h3 className="details__summary--label--title">{bfTranslate('Created on')}</h3>
                <span className="details__summary--copy details__created-on">{localizeDateTime(createdAt)}</span>
              </label>
              <label className="details__summary--label">
                <h3 className="details__summary--label--title">{bfTranslate('Last run on')}</h3>
                <span className="details__summary--copy details__last-executed">
                  {lastExecuted ? localizeDateTime(lastExecuted) : bfTranslate('Never')}
                </span>
              </label>
            </div>
          </>
        )
      }
      <AutomationForm
        automationKey={automationKey}
        editOrCreate={isCopy ? EditOrCreate.Copy : EditOrCreate.Edit}
        goHome={(): void => setView(Views.Home)}
        initialValues={{
          actions: transformIncludedActions(getAutomationFetch.response.included),
          name: `${isCopy ? `${bfTranslate('Copy')} ` : ''}${getAutomationFetch.response.data.attributes.name}`,
          ...transformTriggers(getAutomationFetch.response.data.attributes.triggers)
        }}
      />
    </div>
  );
};
